import { cloneDeep } from 'lodash';
import {
  Entity,
  Field,
  FieldId,
  FieldType,
  Filter,
  IntegerField,
  PersistenceType,
  Row,
  SecurityAction,
  SecurityRule,
  SecurityRuleWithSpecialAction,
} from './types';
import { isBlank } from './GeneralUtils';

export const KEY_SEPARATOR = '|';

export const applyBackEndDefaults = <T extends Row>(entity: Entity<T>, row: T): void => {
  const mutableRow = row as Record<string, unknown>;
  Object.keys(entity.fields).forEach((fieldId: FieldId) => {
    const field = entity.fields[fieldId as keyof T];
    if (
      mutableRow[fieldId] === undefined &&
      [PersistenceType.BackEndGenerated, PersistenceType.BackEndManaged, PersistenceType.BackEndOnly].indexOf(
        field.persistence || PersistenceType.Normal,
      ) > -1
    ) {
      if (field.defaultVal !== undefined) {
        mutableRow[fieldId] = field.defaultVal;
      } else if (field.type === FieldType.Boolean) {
        mutableRow[fieldId] = false;
      }
    }
  });
};

export const cloneEntity = <T extends Row>(entity: Entity<T>): Entity<T> => {
  if (entity.isCloned) {
    throw new Error(`${entity.id} is already cloned`);
  }
  const out = cloneDeep(entity);
  out.isCloned = true;
  return out;
};

export const createData = <T extends Row>(entity: Entity<T>): T => {
  const out: Record<string, unknown> = {};
  Object.keys(entity.fields).forEach((fieldId: FieldId) => {
    const field = entity.fields[fieldId as keyof T];
    const defaultVal =
      field.defaultVal !== undefined ? field.defaultVal : field.type === FieldType.Boolean ? false : null;
    out[fieldId] = defaultVal;
  });
  return out as T;
};

export const doFrontEndEvaluates = <T extends Row>(entity: Entity<T>, data: T): T => {
  if (!data) {
    return data;
  }
  const out: Record<string, unknown> = {};
  getFieldArray(entity).forEach(([_field, fieldId]) => {
    const newVal = (data as Record<string, unknown>)[fieldId];
    // trim() as pg client doesn't; revert action of koa-xss-sanitizer
    out[fieldId] = typeof newVal === 'string' ? newVal.trim().replace(/&amp;/g, '&') : newVal;
  });
  // apply evaluateFrontEndIn() functions
  getFieldArray(entity)
    .filter(([field, _fieldId]) => !!field.evaluateFrontEndIn)
    .forEach(([field, fieldId]) => {
      out[fieldId] = field.evaluateFrontEndIn!(out);
    });
  return out as T;
};

export const getField = <T extends Row>(entity: Entity<T>, fieldId: FieldId): Field =>
  entity.fields[fieldId as keyof T];

export const getFieldArray = <T extends Row>(entity: Entity<T>): [Field, FieldId][] => {
  const out: [Field, FieldId][] = Object.keys(entity.fields).map((fieldId: FieldId) => [
    entity.fields[fieldId as keyof T],
    fieldId,
  ]);
  out.sort((a: [Field, FieldId], b: [Field, FieldId]) => a[0].seq - b[0].seq);
  return out;
};

export const getFilterArrayFromKey = <T extends Row>(entity: Entity<T>, key: string): Filter[] => {
  const keyParts = key.split(KEY_SEPARATOR);
  if (keyParts.length !== entity.primaryKeyFields.length || keyParts.findIndex((part) => !part) !== -1) {
    throw new Error(`invalid key: ${key} for entity ${entity.id}`);
  }
  return entity.primaryKeyFields.map((fieldId: string, index: number) => ({
    fieldId,
    operator: 'eq',
    value: keyParts[index],
  }));
};

export const getMatchingRule = <T extends Row>(
  entity: Entity<T>,
  role: string,
  action: SecurityAction,
  specialAction: string,
): SecurityRule | undefined => {
  const isWrite = ['create', 'update', 'delete'].indexOf(action) > -1;
  return entity.security?.find((rule) => {
    return (
      (rule.role === role || rule.role === 'all' || (role !== 'none' && rule.role === 'all-authed')) &&
      (action !== 'special' || specialAction === (rule as SecurityRuleWithSpecialAction).special) &&
      (rule.action === action || rule.action === 'all' || (rule.action === 'write' && isWrite))
    );
  });
};

export const getRowKey = <T extends Row>(entity: Entity<T>, data: Partial<T>): string => {
  const dataRecord = data as Record<keyof T, unknown>;
  return entity.primaryKeyFields
    .map((fieldId) => {
      const valueAny = dataRecord[fieldId as keyof T];
      const keyPart = isBlank(valueAny)
        ? undefined
        : entity.fields[fieldId as keyof T].type === FieldType.Datetime
          ? new Date(String(valueAny)).toJSON()
          : String(valueAny);
      if (!keyPart) {
        throw new Error(`blank key part from field ${String(fieldId)} of entity ${entity.id}`);
      }
      return keyPart;
    })
    .join(KEY_SEPARATOR);
};

export const getRowDataFromKey = <T extends Row>(entity: Entity<T>, key: string): Partial<T> => {
  const keyParts = key.split(KEY_SEPARATOR);
  if (keyParts.length !== entity.primaryKeyFields.length || keyParts.find((part) => !part)) {
    throw new Error(`invalid key: ${key} for entity ${entity.id}`);
  }
  const out: Record<string, unknown> = {};
  entity.primaryKeyFields.forEach((fieldId, index) => {
    out[fieldId] = keyParts[index];
  });
  return out as Partial<T>;
};

export type RowLabelType = 'Reference' | 'PageTitle' | 'DropdownItem' | 'AutocompleterItem' | 'Dynamic' | 'Static';
export const getRowLabelFunction = <T extends Row>(
  entity: Entity<T>,
  type: RowLabelType,
): ((row: T) => string) | undefined =>
  type === 'Reference'
    ? entity.rowLabelReference || entity.rowLabelDynamic || entity.rowLabel
    : type === 'PageTitle'
      ? entity.rowLabelPageTitle || entity.rowLabel
      : type === 'DropdownItem'
        ? entity.rowLabelDropdownItem || entity.rowLabel
        : type === 'AutocompleterItem'
          ? entity.rowLabelAutocompleterItem || entity.rowLabel
          : type === 'Dynamic'
            ? entity.rowLabelDynamic || entity.rowLabel
            : entity.rowLabel;

export const getRowLabel = <T extends Row>(entity: Entity<T>, type: RowLabelType, data?: T): string =>
  data ? getRowLabelFunction(entity, type)?.(data) || `[unknown]` : `[unknown]`;

export const getTitlePlural = (entity: Entity<Row>) => entity.pluralTitle || `${entity.title}s`;

export const isAllowAddRows = <T extends Row>(entity: Entity<T>) => entity.allowAddRows !== false;

export const isAllowDeleteRows = <T extends Row>(entity: Entity<T>) => entity.allowDeleteRows !== false;

export const isAutoIncrement = <T extends Row>(entity: Entity<T>): boolean =>
  entity.primaryKeyFields.reduce(
    (prev, curr) => prev || !!(entity.fields[curr as keyof T] as IntegerField).autoIncrement,
    false,
  );

export const isKeyComplete = <T extends Row>(entity: Entity<T>, data: T): boolean => {
  const rowData = data as Record<string, unknown>;
  return entity.primaryKeyFields.reduce((prev: boolean, curr: string) => (prev && !!rowData[curr]) || false, true);
};

export const isKeyField = <T extends Row>(entity: Entity<T>, fieldId: FieldId): boolean => {
  return entity.primaryKeyFields.indexOf(fieldId) > -1;
};
